import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";

const navLinks = [
    { label: "Experience", to: "/experience-smart-living", testId: "nav-experience" },
    { label: "Calculator", to: "/calculator", testId: "nav-calculator" },
    { label: "About", to: "/about", testId: "nav-about" },
    { label: "Warranty", to: "/warranty", testId: "nav-warranty" },
    { label: "Contact", to: "/contact", testId: "nav-contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const nav = useNavigate();
    const loc = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [loc.pathname]);

    return (
        <header
            className={`sticky top-0 z-50 transition-colors duration-300 ${
                scrolled ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/70 shadow-sm" : "bg-white/60 backdrop-blur-md"
            }`}
            data-testid="site-navbar"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center" data-testid="nav-home-logo">
                    <Logo height={40} />
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {navLinks.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            data-testid={l.testId}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                loc.pathname === l.to
                                    ? "text-[#1B2E7A] bg-slate-100"
                                    : "text-slate-700 hover:text-[#1B2E7A] hover:bg-slate-100"
                            }`}
                        >
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => nav("/contact")}
                        data-testid="nav-cta-consultation"
                        className="hidden sm:inline-flex items-center gap-1.5 bg-[#1B2E7A] hover:bg-[#13215A] text-white rounded-full px-5 py-2.5 text-sm font-medium transition-[background-color,transform,box-shadow] shadow-sm hover:shadow-md hover:-translate-y-0.5"
                    >
                        Get Free Consultation
                        <ArrowUpRight className="w-4 h-4" />
                    </button>
                    <button
                        className="lg:hidden p-2 rounded-lg text-[#1B2E7A]"
                        aria-label="Toggle menu"
                        onClick={() => setOpen(!open)}
                        data-testid="nav-mobile-toggle"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="nav-mobile-panel">
                    <div className="px-5 py-4 space-y-1">
                        {navLinks.map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                className="block px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-100 font-medium"
                                data-testid={`m-${l.testId}`}
                            >
                                {l.label}
                            </Link>
                        ))}
                        <Link
                            to="/contact"
                            className="block mt-2 px-4 py-3 rounded-xl bg-[#1B2E7A] text-white text-center font-medium"
                            data-testid="m-nav-cta-consultation"
                        >
                            Get Free Consultation
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
